import { ref, onUnmounted } from 'vue';

/**
 * useScrollLock composable
 * Disables/enables page scroll on body without using overflow hidden.
 * Prevents scroll via wheel, touchmove, and keydown events unless a scrollable element is targeted.
 */
export function useScrollLock() {
  const isLocked = ref(false);

  // List of key codes that can cause scroll
  const scrollKeys = [
    'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
    'Space', 'PageUp', 'PageDown', 'Home', 'End'
  ];

  // Helper to check if an element is scrollable
  function isElementScrollable(el: any) {
    if (!el || el === document.body) return false;
    const style = window.getComputedStyle(el);
    const overflowY = style.overflowY;
    const isScrollable = (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight;
    return isScrollable;
  }

  // Helper to check if an element is an input field that needs arrow key navigation
  function isInputField(el: HTMLElement): boolean {
    if (!el) return false;
    
    // Check if it's an input, textarea, or select element
    if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') {
      return true;
    }
    
    // Check if it's contenteditable
    if (el.getAttribute('contenteditable') === 'true') {
      return true;
    }
    
    // Check if it has a role that indicates it's an input field
    const role = el.getAttribute('role');
    if (role === 'textbox' || role === 'combobox' || role === 'searchbox') {
      return true;
    }
    
    return false;
  }

  function preventBodyScroll(e: Event) {
    // Allow scroll if the event target is a scrollable element
    let el = e.target as HTMLElement | null;
    while (el && el !== document.body) {
      if (isElementScrollable(el)) return;
      el = el.parentElement;
    }
    e.preventDefault();
    return false;
  }

  function preventBodyKeyScroll(e: KeyboardEvent) {
    if (!(scrollKeys.includes(e.code) || scrollKeys.includes(e.key))) return;
    
    // Allow arrow keys in input fields
    if (isInputField(e.target as HTMLElement)) {
      return;
    }
    
    let el = e.target as HTMLElement | null;
    while (el && el !== document.body) {
      if (isElementScrollable(el)) return;
      el = el.parentElement;
    }
    e.preventDefault();
    return false;
  }

  function disableScroll() {
    if (isLocked.value) return;
    window.addEventListener('wheel', preventBodyScroll, { passive: false });
    window.addEventListener('touchmove', preventBodyScroll, { passive: false });
    window.addEventListener('keydown', preventBodyKeyScroll, { passive: false });
    isLocked.value = true;
  }

  function enableScroll() {
    if (!isLocked.value) return;
    window.removeEventListener('wheel', preventBodyScroll);
    window.removeEventListener('touchmove', preventBodyScroll);
    window.removeEventListener('keydown', preventBodyKeyScroll);
    isLocked.value = false;
  }

  // Clean up on unmount
  onUnmounted(() => {
    enableScroll();
  });

  return {
    disableScroll,
    enableScroll,
    isLocked,
  };
} 